/* eslint-disable camelcase */
import React, { useContext, useEffect, useState } from 'react';

// MUI Components and Icons
import { Close } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    Stack,
    TextField,
    Typography
} from '@mui/material';

// Third-Party Libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Services/Store
import {
    AspyreDateTimePicker as DateTimePicker,
    AspyreDatePicker as DatePicker,
    AspyreTimePicker as TimePicker
} from 'components/common/InputFields';
import moment from 'moment';
import AttendanceContext from './attendanceContext';
import Spinner from 'ui-component/Spinner';

const EditPopover = ({ isOpen, data, handleClose, parent }) => {
    const { handleUpdateAttendance, loadingUpdateAttendance } = useContext(AttendanceContext);

    const validation = yup.object().shape({
        field: yup.string(),
        check_in_time: yup
            .date()
            .nullable()
            .when('field', {
                is: 'check_in_time',
                then: yup
                    .date()
                    .required('Present Time is required')
                    .typeError('Please Enter a Valid Time')
                    .test('is-different-time', 'Present Time should be less than or equal to Picked Up Time', (value, { parent }) => {
                        const { check_out_time } = parent;
                        return value && check_out_time ? new Date(value) < new Date(check_out_time) : true;
                    })
            }),
        check_out_time: yup
            .date()
            .nullable()
            .when('field', {
                is: 'check_out_time',
                then: yup
                    .date()
                    .required('Picked Up Time is required')
                    .typeError('Please Enter a Valid Time')
                    .test('is-different-time', 'Picked Up Time should be greater than or equal to Present Time', (value, { parent }) => {
                        const { check_in_time } = parent;
                        return value && check_in_time ? new Date(value) > new Date(check_in_time) : true;
                    })
            })
    });

    const formik = useFormik({
        initialValues: {
            field: '',
            check_in_time: null,
            check_out_time: null
        },
        validationSchema: validation,
        onSubmit: async (values) => {
            try {
                handleUpdateAttendance(
                    data?.student_id,
                    data?.classroom_location_id,
                    parent,
                    data?.attendance_id,
                    data?.key === 'check_in_time'
                        ? {
                              updated_start_time: formik?.values?.check_in_time
                                  ? moment(new Date(formik?.values?.check_in_time)).utc().format('YYYY-MM-DD HH:mm')
                                  : null
                          }
                        : {
                              updated_end_time: formik?.values?.check_out_time
                                  ? moment(new Date(formik?.values?.check_out_time)).utc().format('YYYY-MM-DD HH:mm')
                                  : null
                          },
                    handleClose
                );
            } catch (error) {
                handleClose();
            }
        }
    });

    useEffect(() => {
        const presentTime = data?.check_in_time ? moment.utc(data?.check_in_time).local() : null;
        const pickupTime = data?.check_out_time ? moment.utc(data?.check_out_time).local() : null;
        formik.setValues({
            ...formik?.values,
            field: data?.key,
            check_in_time: presentTime,
            check_out_time: pickupTime
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
        <>
            {loadingUpdateAttendance && <Spinner />}
            <Dialog fullWidth maxWidth="xs" open={isOpen}>
                <DialogTitle>
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}>
                        <Typography variant="h3">Edit Attendance Time</Typography>
                        <IconButton onClick={handleClose}>
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <FormControl fullWidth>
                        <TimePicker
                            required
                            fullWidth
                            label={`${data?.label}`}
                            format="hh:mm A"
                            maxTime={formik?.values?.check_out_time}
                            value={formik?.values?.[data?.key]}
                            onChange={(value) => {
                                formik.setFieldValue(`${data?.key}`, value);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={formik.touched?.[data?.key] && Boolean(formik.errors?.[data?.key])}
                                    helperText={formik.touched?.[data?.key] && formik.errors?.[data?.key]}
                                />
                            )}
                        />
                    </FormControl>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button
                        size="small"
                        onClick={() => {
                            formik.resetForm();
                            handleClose();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={loadingUpdateAttendance}
                        size="small"
                        autoFocus
                        variant="contained"
                        onClick={() => {
                            formik.handleSubmit();
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EditPopover;
